/**
 * The rulebook terms on Ops pages, explained (FLOW-348, DOC-80 UI-DA-80-06).
 *
 * 🔒 **ONE list, read by every Ops page.** UI holds the words; the build carries
 * them here as data and no page writes a definition of its own — the term
 * marker (`app-term`) and the *What these terms mean* panel both read this
 * list. UI amends it by addition; a changed explanation takes a new entry
 * version. The words are DOC-80 §6's, verbatim — Ops-only, never on a client
 * page (DOC-58 §6).
 *
 * `appliedIn` names the part of the run record that holds how the term's rule
 * was applied to an application (FLOW-347's basis), for *In this case*. A term
 * without one shows *What it means* alone.
 */

/** Where *In this case* reads from, on the assessment record. */
export type GlossaryAppliedIn = 'amount' | 'receipts' | 'hold';

export interface GlossaryEntry {
  /** Stable key a page marks the term by. */
  readonly key: string;
  readonly term: string;
  readonly meaning: string;
  readonly forYou: string;
  /** The rule reference, as DOC-80 §6 gives it. */
  readonly rule: string;
  /** The entry's own version — a changed explanation is a new version. */
  readonly version: string;
  readonly appliedIn?: GlossaryAppliedIn;
}

/** The glossary's source, carried on a *Flag this* from a term. */
export const GLOSSARY_SOURCE = 'DOC-80 v1.0';

export const GLOSSARY: readonly GlossaryEntry[] = [
  {
    key: 'assessment',
    term: 'The assessment',
    meaning:
      "The system's reading of the application: it applies the rulebook to what the business submitted and produces a recommendation, a confidence band, an amount where one can be worked out, and the reasons for each.",
    forYou:
      'It is advisory. The decision is yours. Its lists tell you what CapX still has to do and what to ask the client for; they are not findings against the client.',
    rule: 'Kickstart prompt §7; DOC-79',
    version: '1.0',
  },
  {
    key: 'pre-assessment',
    term: 'Pre-assessment',
    meaning:
      'The first assessment, run on the statements alone before Ops has asked the funder question. It is worked out as if no funder had been declared, and is expressly provisional.',
    forYou:
      'A figure from it may change once you have asked about funders and decided any flagged receipts. Nothing binding is offered on it.',
    rule: 'FC-DET-048',
    version: '1.0',
  },
  {
    key: 'recommendation',
    term: 'Recommendation',
    meaning:
      'What the assessment proposes: approve, more information needed, decline, or referred with its reason.',
    forYou:
      'A proposal, not an outcome. Record your own decision where the page provides for it.',
    rule: 'Prompt §7, §8',
    version: '1.0',
  },
  {
    key: 'referral',
    term: 'Referred (a referral)',
    meaning:
      'The assessment stopping because something it needed could not be evaluated, and handing the file to a person. It names what it could not evaluate.',
    forYou:
      'A referral is a proper outcome, not a failure of the client or of the assessment. Read its reason; it tells you what a person must look at.',
    rule: 'Prompt §8, §9',
    version: '1.0',
  },
  {
    key: 'advisory',
    term: 'Advisory',
    meaning: "Produced to inform a person's decision, not to make it.",
    forYou: 'Every output of the assessment is advisory on an Ops page.',
    rule: 'DOC-79 §3',
    version: '1.0',
  },
  {
    key: 'confidence-band',
    term: 'Confidence band',
    meaning:
      'How far the assessment trusts its own reading, in three bands — high, medium, low — from the quality and completeness of what it was given. Low means the figures are advisory only and the file refers. Confidence can only restrict an outcome, never permit one it would otherwise refuse.',
    forYou:
      'A low band is a reason to look closer, not a judgement on the client. You see the band and what went into it, never a number.',
    rule: '§B6.8; DOC-54 §5',
    version: '1.0',
  },
  {
    key: 'amount',
    term: 'The amount (sizing)',
    meaning:
      "How much the rules allow CapX to lend on this application: the sizing test, with any reductions applied, kept within the product's floor and ceiling.",
    forYou:
      'No amount yet means a rule could not be worked out, with the reason beside it — not that the business qualifies for nothing.',
    rule: '§B6.5, §B6.4, §A12.3',
    version: '1.0',
    appliedIn: 'amount',
  },
  {
    key: 'affordability-cap',
    term: 'Affordability cap',
    meaning:
      'The single sizing test: the largest monthly instalment the business can carry, set as a share of its stressed median monthly revenue. The share is higher where revenue is stable month to month and lower where it varies.',
    forYou:
      'If the cap could not be worked out, the amount cannot be. The reason is usually that revenue variation could not be measured.',
    rule: '§B6.5',
    version: '1.0',
    appliedIn: 'amount',
  },
  {
    key: 'stressed-median-revenue',
    term: 'Stressed median monthly revenue',
    meaning:
      "The business's typical month of revenue (the middle month, not the average), reduced by a fixed stress before the cap is applied, so that the cap is set on a cautious month.",
    forYou:
      "The figure in In this case is after the stress; it will be lower than the statements' typical month.",
    rule: '§B6.5, §B6.2',
    version: '1.0',
    appliedIn: 'amount',
  },
  {
    key: 'revenue-variation',
    term: 'Revenue variation',
    meaning:
      'How much monthly revenue moves from month to month, measured across the assessment period. Below a set level revenue is stable; at or above it, volatile.',
    forYou:
      'It decides which share of revenue the cap uses. Where it could not be measured, the assessment says so and no amount is worked out.',
    rule: '§B6.5; prompt §9',
    version: '1.0',
    appliedIn: 'amount',
  },
  {
    key: 'reduction',
    term: 'Reduction (reduction log)',
    meaning:
      'A cut the rules apply to the sized amount for a stated reason — customer concentration, exposure already with CapX, trading history — each at a set rate. The log lists each factor, its rate and the value after it.',
    forYou:
      "Read the log to see why an amount is lower than the cap allows. Reductions are the rules', not the assessment's opinion.",
    rule: '§B6.4; prompt §7',
    version: '1.0',
    appliedIn: 'amount',
  },
  {
    key: 'product-floor-ceiling',
    term: 'Product floor and ceiling',
    meaning:
      'The smallest and largest amounts the product lends. A worked-out figure below the floor or above the ceiling is not shown as an amount.',
    forYou:
      "Below the product's floor — your decision is a choice the rules leave to you; the screen does not make it.",
    rule: '§B6.5, §A12.3',
    version: '1.0',
  },
  {
    key: 'gross-credits',
    term: 'Gross credits',
    meaning:
      "All money arriving into the business's accounts over the assessment period, before anything is taken out of it.",
    forYou:
      'Thresholds on the statements (5%) are measured against this figure, as the record holds it.',
    rule: 'FC-DET-047, FC-DET-102',
    version: '1.0',
  },
  {
    key: 'assessment-period',
    term: 'Assessment period',
    meaning: 'The months of statements the assessment used.',
    forYou:
      '12 months, 1 account in In this case is this period. Months you accept are recorded by you (Record how many months of statements you accept).',
    rule: 'DOC-73; prompt §1',
    version: '1.0',
  },
  {
    key: 'principal-trading-account',
    term: 'Principal trading account',
    meaning:
      "The uploaded account with the largest credits from outside the business's own accounts over the period. The system derives it; it is not asked. An Authorised Ops user may record a different one with a written reason.",
    forYou:
      'If the assessment chose an account you think is wrong, record the right one with your reason; do not ask the client to choose.',
    rule: 'FC-DET-049',
    version: '1.0',
  },
  {
    key: 'every-account-confirmation',
    term: 'The every-account confirmation',
    meaning:
      "The business's confirmation that it has uploaded statements for every account it holds. Without it no amount is worked out and the file is held.",
    forYou:
      'A held file shows the client being processed — CapX will be in contact. You ask; the client is never told it is at fault.',
    rule: 'FC-DET-044, FC-DET-047',
    version: '1.0',
  },
  {
    key: 'further-account',
    term: "A possible further account of the business's",
    meaning:
      'An account the statements suggest the business also holds, from money moving to or from it. It holds the application only where that money exceeds 5% of gross credits for that counterparty; below that it is information for your conversation.',
    forYou:
      'Named as what may be another account, never as one the client left out. A missing account is never a reason to decline.',
    rule: 'FC-DET-047, FC-DET-102; DOC-77',
    version: '1.0',
  },
  {
    key: 'funder-question',
    term: 'The funder question',
    meaning:
      "CapX's own step: after the pre-assessment, Ops asks every client whether it uses any loan, invoice or debtor finance facility, and records the answer.",
    forYou:
      "Its absence is your outstanding step, never the client's gap. Ask it of every client.",
    rule: 'FC-DET-048; DOC-63',
    version: '1.0',
  },
  {
    key: 'flagged-receipt',
    term: 'Flagged receipt',
    meaning:
      'A credit the assessment marks as possibly not revenue, on words quoted from the statement. It is left out of revenue until an Authorised Ops user decides it, so your decision can only raise or keep the figure.',
    forYou:
      'Decide each flagged receipt with a written reason; sizing does not complete while one is undecided. You may change a decision, with a new reason, until signature.',
    rule: 'FC-DET-045, -083, -084, -085; DOC-65',
    version: '1.0',
    appliedIn: 'receipts',
  },
  {
    key: 'worked-out-without',
    term: 'Worked out without',
    meaning:
      'What the assessment computed the figure without — the funder answer not yet asked, receipts left out, a measure it could not take.',
    forYou: 'Each is a reason the figure may move; none is a shortfall in the client.',
    rule: 'FC-DET-048, FC-DET-084; prompt §9',
    version: '1.0',
  },
  {
    key: 'outstanding-item',
    term: 'Outstanding item (and its owner)',
    meaning:
      'Something the record holds as still needed before the assessment can finish, with who owes it: CapX or the business.',
    forYou:
      "CapX's items are your steps; the business's are what you ask the client for, in the words the client's page uses. The screen sorts nothing.",
    rule: 'DOC-73, DOC-79 §2',
    version: '1.0',
  },
  {
    key: 'held',
    term: 'Held',
    meaning:
      'The application waiting on a condition the rules name — most often the every-account confirmation or a large transfer to a possible further account — with no figure shown meanwhile.',
    forYou: 'The client sees being processed. What clears the hold is shown beside it; you ask.',
    rule: 'FC-DET-044, FC-DET-047; DOC-59 §5',
    version: '1.0',
    appliedIn: 'hold',
  },
  {
    key: 'publishable',
    term: 'Publishable (the first look)',
    meaning:
      "A figure the record marks as fit to reach the agent and client, under the publication rules: labelled by the evidence it rests on, within the product's limits, and with the every-account confirmation on file.",
    forYou:
      'Only a publishable figure may be told to the client, with its standing line. Where none is marked, say nothing about an amount.',
    rule: '§A12.2, §A12.3, FC-DET-044; DOC-78',
    version: '1.0',
  },
  {
    key: 'ai-consent',
    term: 'Consent to AI assessment',
    meaning:
      "The business's consent for what it submits — and what Ops records from it — to be read by the assessment. CapX's own material needs none.",
    forYou:
      "Without it on record, the assessment should not run on the business's documents; it is an outstanding item of the business.",
    rule: 'FC-DET-103',
    version: '1.0',
  },
  {
    key: 'authorised-ops-user',
    term: 'Authorised Ops user',
    meaning:
      'Any trained Ops person, at the everyday level; a floor, not a specific tier. Includes analysts.',
    forYou: 'If an action says Authorised Ops user, any Ops tier may take it.',
    rule: 'Ops Guide Part C',
    version: '1.0',
  },
  {
    key: 'run-as-at',
    term: 'Run · as-at',
    meaning:
      'One execution of the assessment, and the moment it ran. Each page line names the run it came from.',
    forYou: 'Two runs can differ; check the as-at before comparing or flagging.',
    rule: 'DOC-79 §3',
    version: '1.0',
  },
  {
    key: 'versions',
    term: 'Rulebook version · prompt version',
    meaning:
      "The version of the rules, and of the assessment's instructions, a run used. Both are recorded per run.",
    forYou:
      'They travel with every flag, so the development side knows which rules the run read.',
    rule: 'DOC-79 §5A',
    version: '1.0',
  },
];

const BY_KEY = new Map(GLOSSARY.map((entry) => [entry.key, entry]));

/** The entry for `key`, or undefined — a page marking an unlisted key shows plain text. */
export function glossaryEntry(key: string): GlossaryEntry | undefined {
  return BY_KEY.get(key);
}
